import { ConfiguratorType } from "../ConfiguratorType";
import { getHighestVisitedStep, getKmats } from "../Linear/redux/selectors";
import { sendCarriageUpdate } from "./api/sendCarriageConfiguratorUpdate";
import { sendCarriageMiscChange, sendCarriageStepChange } from "./api/sendCarriageStepChange";
import { sendCarriageDataReset } from "./api/sendCarriageStepReset";
import { sendCarriageResetConfigurator } from "./api/sendCarriageResetConfigurator";
import { sendCarriageAddToCart } from "./api/sendCarriageAddToCart";
import { CarriageSummaryData } from "./components/CarriageSummaryData";
import { type RootState } from "../../redux/store";
import {
  CARRIAGE_LOCATING_CSTIC_NAME,
  CARRIAGE_MIN_STEP_TO_BUY,
  CARRIAGE_MIN_STEP_TO_SAVE,
  FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
  FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE,
} from "./carriageConfiguratorConstants";
import { type LinearCsticValue, type SubstepTile } from "../Linear/models";
import {
  BASE_DESIGNATION_SEPARATOR,
  CENTRAL_CONNECTORS,
  CENTRAL_CONNECTORS_LABEL,
  CENTRAL_CONNECTORS_VALUE,
  CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_DESIGN_TYPE_SUBLEVEL_NAME,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_WAGENBAUFORM_NAME,
  MANUAL_CONNECTORS,
  MANUAL_CONNECTORS_LABEL,
  MANUAL_CONNECTORS_VALUE,
} from "../Linear/constants";
import { trimCollapseWhitespace } from "../Linear/utils";
import { type ConfiguratorPageDefinition, type StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import { grabXmlTranslation } from "~/Configurators/grabExternalTranslation";
import { fetchSimpleSubstepValue } from "~/Configurators/Linear/components/fetchSimpleSubstepValue";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import * as CarriageCstic from "~/Configurators/Carriage/v2/configuration/CarriageCstics";
import { pushStandardValueIfMissing } from "~/Configurators/common/v2/helper";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/Configurators/Shaft/shaftGaHelper";
import { addFakeCstic } from "~/Configurators/transformerHelper";
import { CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "../constants";

const CARRIAGE_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);

      const carriageKmat = getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;

      const designTypeCstic = carriageKmat[CSTIC_DESIGN_TYPE_SUBLEVEL_NAME];
      const sizeCstic = carriageKmat["VBGR_065"];

      const selectionData = {
        series: designTypeCstic.value,
        size: sizeCstic.value,
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
  },
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const substeps = state.linearConfigurator.substeps;

      const system_coating = grabXmlTranslation(summaryDataProvider, fetchSimpleSubstepValue(substeps.Coating) ?? "");
      const carriage_type = grabXmlTranslation(
        summaryDataProvider,
        fetchSimpleSubstepValue(substeps.CarriageType) ?? "",
      );
      const preload = grabXmlTranslation(summaryDataProvider, fetchSimpleSubstepValue(substeps.Preload) ?? "");
      const accuracy = grabXmlTranslation(summaryDataProvider, fetchSimpleSubstepValue(substeps.Accuracy) ?? "");

      const selectionData = {
        system_coating: trimCollapseWhitespace(system_coating),
        carriage_type: trimCollapseWhitespace(carriage_type),
        preload: trimCollapseWhitespace(preload),
        accuracy: trimCollapseWhitespace(accuracy),
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
  },
  {
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);

      const substeps = state.linearConfigurator.substeps;
      const kmats = getKmats(state);
      const carriageKmat = getMainKmat({ kmats });

      const lubricationSubstep = substeps.Lubrication;
      const lubricationTiles: SubstepTile[] = [...lubricationSubstep.tiles];
      lubricationSubstep.groupedTiles.forEach((group) => lubricationTiles.push(...group.tiles));
      const lubricationSelectedTile = lubricationTiles[lubricationSubstep.selectedTile!];

      const smdsTile =
        substeps.SMDS.isVisible && substeps.SMDS.selectedTile !== null
          ? substeps.SMDS.tiles[substeps.SMDS.selectedTile!]
          : null;
      const smdeTile =
        substeps.SMDE.isVisible && substeps.SMDE.selectedTile !== null
          ? substeps.SMDE.tiles[substeps.SMDE.selectedTile!]
          : null;

      const locatingFaceSubstep = substeps.LocatingFace;
      const locatingFaceCstic = carriageKmat.cstics[CARRIAGE_LOCATING_CSTIC_NAME];
      const locatingFaceTile = locatingFaceCstic.values.find((val) => val.value === locatingFaceCstic.value);

      const singleCarriageReference = carriageKmat.cstics[CSTIC_WAGENBAUFORM_NAME];
      const carriageRefSelectedTile = singleCarriageReference.values.find(
        (singleValue) => singleValue.value === singleCarriageReference.value,
      );

      const kitLeft = carriageKmat.cstics[CSTIC_CARRIAGE_LEFT_NAME];
      const kitMid = carriageKmat.cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
      const kitRight = carriageKmat.cstics[CSTIC_CARRIAGE_RIGHT_NAME];

      const lubricationConnectorCstic = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
      const lubricationConnectorSelectedTile = lubricationConnectorCstic?.values.find(
        (singleValue) => singleValue.key === lubricationConnectorCstic.value,
      );

      const lubricationPosition = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME];
      const lubricationPositionSelectedTile = lubricationPosition?.values.find(
        (singleValue) => singleValue.key === lubricationPosition.value,
      );

      const lubricationPortSelection = carriageKmat.cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME];
      const lubricationPortSelectionSelectedTile = lubricationPortSelection?.values.find(
        (singleValue) => singleValue.key === lubricationPortSelection.value,
      );

      const lubricationDegree = carriageKmat.cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];
      const lubricationDegreeSelectedTile = lubricationDegree?.values.find(
        (singleValue) => singleValue.key === lubricationDegree.value,
      );

      const SMDE = grabXmlTranslation(summaryDataProvider, lubricationSelectedTile.name ?? "-");
      const lubrification = grabXmlTranslation(summaryDataProvider, lubricationConnectorSelectedTile?.label ?? "-");
      const SMDS = grabXmlTranslation(summaryDataProvider, smdsTile?.name ?? "-");
      const locating_face = grabXmlTranslation(
        summaryDataProvider,
        locatingFaceTile?.label ?? locatingFaceCstic.value ?? "-",
      );
      const carriage_type = grabXmlTranslation(summaryDataProvider, carriageRefSelectedTile?.label ?? "-");
      const kit_left = grabXmlTranslation(summaryDataProvider, kitLeft?.value ?? "-");
      const kit_centre = grabXmlTranslation(summaryDataProvider, kitMid?.value ?? "-");
      const kit_right = grabXmlTranslation(summaryDataProvider, kitRight?.value ?? "-");
      const lubrification_connector = grabXmlTranslation(
        summaryDataProvider,
        lubricationConnectorSelectedTile?.label ?? "-",
      );
      const lubrification_port_alignment = grabXmlTranslation(
        summaryDataProvider,
        lubricationPositionSelectedTile?.label ?? "-",
      );
      const lubrification_port_selection = grabXmlTranslation(
        summaryDataProvider,
        lubricationPortSelectionSelectedTile?.label ?? "-",
      );
      const lubrification_port_degree = grabXmlTranslation(
        summaryDataProvider,
        lubricationDegreeSelectedTile?.label ?? "-",
      );

      const selectionData = {
        lubrification: trimCollapseWhitespace(lubrification),
        SMDS: trimCollapseWhitespace(SMDS),
        SMDE: trimCollapseWhitespace(SMDE),
        locating_face: trimCollapseWhitespace(locating_face),
        carriage_type: trimCollapseWhitespace(carriage_type),
        kit_left: trimCollapseWhitespace(kit_left),
        kit_centre: trimCollapseWhitespace(kit_centre),
        kit_right: trimCollapseWhitespace(kit_right),
        lubrification_connector: trimCollapseWhitespace(lubrification_connector),
        lubrification_port_alignment: trimCollapseWhitespace(lubrification_port_alignment),
        lubrification_port_selection: trimCollapseWhitespace(lubrification_port_selection),
        lubrification_port_degree: trimCollapseWhitespace(lubrification_port_degree),
      };
      sendStepCompletedEvent(summaryDataProvider.getGaStepName(3), selectionData);
    },
  },
];

export const CARRIAGE_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Carriage,
  productCode: CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.carriage.name",
  },
  apiCalls: {
    update: sendCarriageUpdate,
    reset: sendCarriageResetConfigurator,
    stepChange: sendCarriageStepChange,
    stepReset: sendCarriageDataReset,
    miscChange: sendCarriageMiscChange,
    addToCart: sendCarriageAddToCart,
  },
  summary: CarriageSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= CARRIAGE_MIN_STEP_TO_BUY;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= CARRIAGE_MIN_STEP_TO_SAVE;
  },
  stepHardcodedData: CARRIAGE_CONFIGURATOR_STEP_DATA,
  generateDesignation: (kmats) => {
    /*
    According to bug SCHAEFF-17623, if VVER_065 suffix is "/" or null, do not include it in the base designation
   */
    const kmat = getMainKmat({ kmats });

    let designType = kmat.cstics[CSTIC_DESIGN_TYPE_SUBLEVEL_NAME]?.value;
    const size = kmat.cstics["VBGR_065"]?.value ?? "";
    let suffix = kmat.cstics["VVER_065"]?.value?.toUpperCase();

    if (!designType) {
      return BASE_DESIGNATION_SEPARATOR;
    }

    if (designType?.includes("..-")) {
      [designType, suffix] = designType.split("..-");
    }

    return [designType + size, suffix]
      .filter(Boolean)
      .filter((item) => item !== "/")
      .join(BASE_DESIGNATION_SEPARATOR);
  },
  gaConfiguratorName: "carriage",
  modelAndUpdatePreprocessor: (model, dto) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    pushStandardValueIfMissing(kmat, CarriageCstic.GreasingPerGreaseGroup, "/");
    pushStandardValueIfMissing(kmat, CarriageCstic.GreasingLType, "/");

    const locatingFaceCstic = cstics[CarriageCstic.LocatingFace];
    if (locatingFaceCstic) {
      locatingFaceCstic.values?.forEach((val) => {
        val.label = `configurator.carriage.cstic.locatingFace.${val.value}.name`;
      });
    }

    let designSuffix = "";
    const designValue = cstics[CarriageCstic.MaterialName]?.value;
    if (designValue === "RWU") {
      designSuffix = ".RWU";
    } else if (designValue === "KWSE") {
      designSuffix = ".KWSE";
    } else if (designValue === "KWVE") {
      designSuffix = ".KWVE";
    } else if (designValue === "KWVE..-W") {
      designSuffix = ".KWVE-W";
    }

    const kitsLeftCstic = cstics[CarriageCstic.KitsLeft];
    if (kitsLeftCstic) {
      kitsLeftCstic.label = "configurator.carriage.cstic.kit.left.name";
      kitsLeftCstic.info = "configurator.carriage.cstic.kit.left.info" + designSuffix;
    }
    const kitsCenterCstic = cstics[CarriageCstic.KitsCenter];
    if (kitsCenterCstic) {
      kitsCenterCstic.label = "configurator.carriage.cstic.kit.center.name";
      kitsCenterCstic.info = "configurator.carriage.cstic.kit.center.info" + designSuffix;
    }

    const kitsRightCstic = cstics[CarriageCstic.KitsRight];
    if (kitsRightCstic) {
      kitsRightCstic.label = "configurator.carriage.cstic.kit.right.name";
      // "left" on purpose
      kitsRightCstic.info = "configurator.carriage.cstic.kit.left.info" + designSuffix;
    }

    const lubricationConnectorStatusCstic = cstics[CarriageCstic.LubricationConnectorOrientation];
    if (lubricationConnectorStatusCstic) {
      lubricationConnectorStatusCstic.label = "configurator.carriage.cstic.lubricationConnector.name";
      lubricationConnectorStatusCstic.values?.forEach((val) => {
        val.label = `configurator.carriage.cstic.lubricationConnectorStatus.${val.value}.name`;
      });
    }

    const lubricationConnectorCstic = cstics[CarriageCstic.LubricationConnector];
    if (lubricationConnectorCstic) {
      lubricationConnectorCstic.label = "configurator.carriage.cstic.lubricationConnector.name";
      lubricationConnectorCstic.values?.forEach((val) => {
        val.label = val.value;
      });
      if (lubricationConnectorCstic?.values) {
        let connectorType;
        if (!kmat.cstics[FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE]) {
          connectorType = determineConnectorType(lubricationConnectorCstic.value ?? "") ?? MANUAL_CONNECTORS_VALUE;
          addFakeCstic(kmat, {
            name: FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE,
            value: connectorType,
            values: [
              {
                value: MANUAL_CONNECTORS_VALUE,
                label: MANUAL_CONNECTORS_LABEL,
                key: MANUAL_CONNECTORS_VALUE,
                info: "",
                imageUrl: "",
              },
              {
                value: CENTRAL_CONNECTORS_VALUE,
                label: CENTRAL_CONNECTORS_LABEL,
                key: CENTRAL_CONNECTORS_VALUE,
                info: "",
                imageUrl: "",
              },
            ],
          });
        } else {
          connectorType = kmat.cstics[FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE]?.value ?? MANUAL_CONNECTORS_VALUE;
        }
        lubricationConnectorCstic.values = filterByConnectorType(lubricationConnectorCstic.values, connectorType);
      }
    }

    const lubricationConnectorOrientationCstic = cstics[CarriageCstic.LubricationConnectorPosition];
    if (lubricationConnectorOrientationCstic) {
      lubricationConnectorOrientationCstic.label = "configurator.carriage.cstic.lubricationConnectorOrientation.name";
      lubricationConnectorOrientationCstic.info = "configurator.carriage.cstic.lubricationConnectorOrientation.info";
      lubricationConnectorOrientationCstic.values?.forEach((val) => {
        val.label = `configurator.carriage.cstic.lubricationConnectorOrientation.${val.value}.name`;
      });
    }

    const angularOrientationCstic = cstics[CarriageCstic.LubricationConnectorAlignment];
    if (angularOrientationCstic) {
      angularOrientationCstic.label = "configurator.carriage.cstic.angularOrientation.name";
      angularOrientationCstic.info = "configurator.carriage.cstic.angularOrientation.info";
      angularOrientationCstic.values?.forEach((val) => {
        const value = val.value.replace("°", "");
        val.label = `configurator.carriage.cstic.angularOrientation.${value}.name`;
      });
    }

    addFakeCstic(
      model,
      FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
      dto.enableConfigureCarriageValues ? "true" : "false",
    );

    return model;
  },
} as const;

const sendStepCompletedEvent = makeSendStepCompletedEvent(CARRIAGE_DEFINITION);

function determineConnectorType(value: string) {
  if (MANUAL_CONNECTORS.includes(value)) {
    return MANUAL_CONNECTORS_VALUE;
  }
  if (CENTRAL_CONNECTORS.includes(value)) {
    return CENTRAL_CONNECTORS_VALUE;
  }
  return null;
}
function filterByConnectorType(values: LinearCsticValue[], connectorType: string): LinearCsticValue[] {
  if (!connectorType) {
    throw new Error("Connector type is undefined");
  }
  const validValues = new Set(connectorType === MANUAL_CONNECTORS_VALUE ? MANUAL_CONNECTORS : CENTRAL_CONNECTORS);
  return values.filter((value) => validValues.has(value.value));
}
